/* eslint-disable no-unused-vars */
import * as types from './types'
import http from '@/plugins/api'
import { startLoading } from '@/plugins/loading'
import store from '@/store'
import globals from '@/plugins/globals'
import SendbirdChat from '@sendbird/chat'
import { GroupChannelModule, GroupChannelHandler, GroupChannelFilter, GroupChannelListOrder, PublicChannelFilter } from '@sendbird/chat/groupChannel'
import moment from 'moment'

const appId = window.config ? window.config.sendbirdAppId : ''

export default {
  setUser({ commit }, user) {
    commit(types.SET_USER, user)
  },
  resetChannels({ commit }) {
    commit(types.RESET_CHANNELS)
  },
  resetChannelInfo({ commit }) {
    commit(types.RESET_CHANNELINFO)
  },
  resetMessages({ commit }) {
    commit(types.RESET_MESSAGES)
  },
  setChatBar({ commit }, data) {
    commit(types.SET_CHATBAR, data)
  },
  addMembers({ commit }, data) {
    commit(types.ADD_MEMBERS, data)
  },
  async initChat({ commit, state }) {
    let profile = store.getters['auth/profile']
    try {
      let guid = profile ? profile.guid : null
      if (guid) {
        // need a user
        commit(types.SET_HANDLER, globals.uuid())
        if (window.sendbird === null || window.sendbird === undefined) {
          // initialize sendbird
          window.sendbird = await SendbirdChat.init({
            appId: appId,
            modules: [new GroupChannelModule()]
          })
          // set up the sendbird event handler
          let groupChannelHandler = new GroupChannelHandler({
            onMessageReceived: (channel, message) => {
              console.log('Message Received', {
                channel: channel,
                message: message,
                channelInfo: state.channelInfo
              })
              if (state.channelInfo) {
                if (channel.url === state.channelInfo.url) {
                  // user created, add to existing messages
                  if (state.messages) {
                    commit(types.SET_A_MESSAGE, {
                      id: message.messageId,
                      name: null,
                      createdOn: moment(message.createdAt).utc().format('MM/DD/YYYY h:mm:ss a'),
                      type: message.sender.userId === profile.guid ? 'received' : 'sent',
                      color: null,
                      message: message.message,
                      member: message.sender.nickname,
                      guid: message.sender.userId
                    })
                  }
                  // trigger a change event
                  commit(types.SET_CHANGED, {
                    name: 'message-received',
                    channel: channel.url
                  })
                }
              }
              store.dispatch('chat/getChannels')
            },
            onMessageUpdated: (channel, message) => {
              console.log('Message Updated', {
                channel: channel,
                message: message
              })
            },
            onMessageDeleted: (channel, messageId) => {
              console.log('Message Deleted', {
                channel: channel,
                messageId: messageId
              })
            },
            onChannelChanged: channel => {
              console.log('Channel Changed', {
                channel: channel
              })
              commit(types.SET_CHANGED, {
                name: 'channel-changed',
                channel: channel.url
              })
            },
            onChannelDeleted: (channelUrl, channelType) => {
              console.log('Channel Deleted', {
                channelUrl: channelUrl,
                channelType: channelType
              })
            },
            onUserJoined: (channel, user) => {
              console.log('User Joined', {
                channel: channel,
                user: user
              })
            },
            onUserLeft: (channel, user) => {
              console.log('User Left', {
                channel: channel,
                user: user
              })
            },
            onTypingStatusUpdated: channel => {
              if (channel.isTyping) {
                const members = channel.getTypingUsers()
                commit(types.SET_TYPING, {
                  url: channel.url,
                  members: members
                })
              } else {
                commit(types.SET_TYPING, null)
              }
              console.log('Channel Typing', {
                channel: channel,
                someoneTyping: state.someoneTyping
              })
            },
            onChannelMemberCountChanged: channels => {
              console.log('Channel Member Count', {
                channels: channels
              })
            }
          })
          await window.sendbird.groupChannel.addGroupChannelHandler(state.handlerId, groupChannelHandler)
        }
        console.log('Chat Initialized', window.sendbird)
        // first, we create the user if they don't already exist (the request will fail if they do)
        await http.post(`chat/CreateChatUser?userId=${profile.user}`)
        // now, we can connect the user
        window.sendbird.connect(guid).then((user, error) => {
          if (user) {
            store.dispatch('chat/setUser', user)
            console.log('Chat Connected', {
              sendbird: window.sendbird,
              user: state.user
            })
            store.dispatch('chat/getUnread')
          } else {
            console.log('Error (initChat) Connect', error)
          }
          window.sendbird.setChannelInvitationPreference(true)
        })
      }
    } catch (e) {
      console.log('Error (initChat)', e)
    }
  },
  async quitChat({ commit, state }) {
    try {
      console.log('Quitting Chat...')
      if (window.sendbird) {
        if (window.sendbird.groupChannel) {
          await window.sendbird.groupChannel.removeGroupChannelHandler(state.handlerId)
        }
        await window.sendbird.disconnect()
      }
      await commit(types.QUIT_CHAT)
    } catch (e) {
      console.log('Error (QUIT_CHAT)', e)
    }
  },
  async getChannels({ commit, state }) {
    let profile = store.getters['auth/profile']
    let filter = null
    commit(types.RESET_CHANNELS)
    try {
      let fetchedChannels = []
      let guid = profile ? profile.guid : null
      if (guid) {
        filter = `adhoc_${profile.apiName}`
        // My Channels first...
        let channelFilter = new GroupChannelFilter()
        channelFilter.includeEmpty = true
        channelFilter.publicChannelFilter = PublicChannelFilter.PRIVATE
        channelFilter.customTypeStartsWithFilter = filter
        channelFilter.setUserIdsFilter = [profile.guid]
        let params = {
          filter: channelFilter,
          order: GroupChannelListOrder.LATEST_LAST_MESSAGE
        }
        let collection = await window.sendbird.groupChannel.createGroupChannelCollection(params)
        do {
          const channels = await collection.loadMore()
          fetchedChannels = fetchedChannels.concat(channels)
          // Update the query for the next iteration if there are more channels
          if (collection.hasMore) {
            collection = collection.loadMore()
          }
        } while (collection.hasMore)
        console.log('Fetched My Channels', fetchedChannels)
        fetchedChannels.forEach(channel => {
          let members = []
          channel.members.forEach(member => {
            members.push({
              name: member.nickname,
              userId: member.userId,
              metadata: member.metaData,
              lastSeenAt: member.lastSeenAt,
              isActive: member.isActive,
              connectionStatus: member.connectionStatus,
              state: member.state
            })
          })
          commit(types.SET_A_MYCHANNEL, {
            url: channel.url,
            name: channel.name,
            type: channel.customType,
            data: channel.data,
            memberCount: channel.memberCount,
            members: members,
            unreadCount: channel.unreadMessageCount,
            createdOn: channel.createdAt,
            unreadMembers: channel.cachedUnreadMemberState,
            metadata: channel.cachedMetaData,
            owner: channel.my_role === 'operator' ? profile.guid : channel.creator ? channel.creator.userId : null,
            isMessages: !!channel.lastMessage
          })
        })
        console.log('Got My Channels', state.mychannels)
        // now, load up the request channels
        fetchedChannels = []
        // filter out only those associated with their "app" (such as, OPUS Commercial, Gerber, Crash)
        filter = `request_${profile.apiName}`
        // for shop users, they can only see their chats
        if (['15', '16', '17'].includes(profile.roles[0])) {
          filter += `_${profile.firm}`
        }
        channelFilter = new GroupChannelFilter()
        channelFilter.includeEmpty = true
        channelFilter.publicChannelFilter = PublicChannelFilter.PUBLIC
        channelFilter.customTypeStartsWithFilter = filter
        channelFilter.includeMetaData = true
        channelFilter.setUserIdsFilter = [profile.guid]
        params = {
          filter: channelFilter,
          order: GroupChannelListOrder.LATEST_LAST_MESSAGE
        }
        collection = await window.sendbird.groupChannel.createGroupChannelCollection(params)
        do {
          const channels = await collection.loadMore()
          fetchedChannels = fetchedChannels.concat(channels)
          // Update the query for the next iteration if there are more channels
          if (collection.hasMore) {
            collection = collection.loadMore()
          }
        } while (collection.hasMore)
        console.log('Fetched Request Channels', fetchedChannels)
        // add the members
        fetchedChannels.forEach(channel => {
          let members = []
          channel.members.forEach(member => {
            members.push({
              name: member.nickname,
              userId: member.userId,
              metadata: member.metaData,
              lastSeenAt: member.lastSeenAt,
              isActive: member.isActive,
              connectionStatus: member.connectionStatus,
              state: member.state
            })
          })
          commit(types.SET_A_CHANNEL, {
            url: channel.url,
            name: channel.name,
            type: channel.customType,
            data: channel.data,
            memberCount: channel.memberCount,
            members: members,
            unreadCount: channel.unreadMessageCount,
            createdOn: channel.createdAt,
            unreadMembers: channel.cachedUnreadMemberState,
            metadata: channel.cachedMetaData,
            owner: null,
            isMessages: !!channel.lastMessage
          })
        })
        console.log('Got Request Channels', state.channels)
        // lastly, fetch all 'active' requests (they can be channels already)
        let options = {
          page: 1,
          offset: 0,
          sortBy: 'desc',
          orderBy: 'createdOn',
          pageSize: 99999,
          searchTerm: null,
          firm: profile.firm,
          filter: '2,3,8,10,11',
          type: null
        }
        try {
          let response = await store.dispatch('calibrates/calibrateEventsActive', options)
          if (response) {
            if (response.items.length > 0) {
              response.items.forEach(element => {
                let url = `${profile.apiName}_${element.firm}_${element.calibrate}_${element.calibrateEvent}`
                // look to see if the user has already joined the session (request)
                let found = state.channels.find(x => x.url === url)
                if (!found) {
                  // if not, we can present the request to allow the user to join
                  commit(types.SET_A_REQUEST, {
                    url: url,
                    name: element.calibrateEvent.toString(),
                    type: 'active',
                    data: '',
                    memberCount: 0,
                    members: [],
                    unreadCount: 0,
                    createdOn: null,
                    unreadMembers: 0,
                    metadata: {
                      calibrate: element.calibrate,
                      calibrateEvent: element.calibrateEvent,
                      firm: element.firm,
                      firmName: element.firmName,
                      make: element.make,
                      model: element.model,
                      ronumber: element.ronumber,
                      serviceFirm: element.serviceFirm,
                      serviceFirmName: element.serviceFirmName,
                      status: element.status,
                      vin: element.vin,
                      year: element.year
                    },
                    owner: null,
                    isMessages: false
                  })
                }
              })
              console.log('Got Requests', state.requests)
            }
          }
        } catch (e) {
          console.log('Error: get requests', e.message)
        }
        // set up the groups
        store.dispatch('chat/setFirmCounts')
        // get unread count
        store.dispatch('chat/getUnread')
      }
    } catch (e) {
      console.log('Error (getChannels)', e)
    }
  },
  setFirmCounts({ commit }) {
    commit(types.SET_FIRMCOUNTS)
  },
  async getUnread({ commit }) {
    let counts = await window.sendbird.groupChannel.getTotalUnreadMessageCount()
    await commit(types.GET_UNREAD, counts)
  },
  async fetchChannel({ commit, state }, info) {
    let profile = store.getters['auth/profile']
    commit(types.SET_CHANNELINFO, info)
    let channel = null
    try {
      if (info.url) {
        channel = await window.sendbird.groupChannel.getChannel(info.url)
      }
    } catch (e) {
      console.log('Fetch Channel - Not Found, Create...', info)
    }
    try {
      if (channel) {
        // if found, see if current user needs to join
        console.log('Fetch Channel - Found', channel)
        if (channel.members.length > 0) {
          let found = channel.members.find(x => x.userId === profile.guid)
          if (!found) {
            channel.join()
          }
        } else {
          channel.join()
        }
      } else {
        // didn't find a channel, create it
        channel = await store.dispatch('chat/createChannel', {
          info: info,
          refresh: true
        })
      }
      // is Channel Info showing 'active'?
      if (state.channelInfo.type === 'active') {
        // if so, we need to update it as a request now that the user has joined
        commit(types.UPDATE_CHANNELINFO, {
          createdOn: moment().format('MM/DD/YYYY hh:mm:ss a'),
          type: 'request',
          member: {
            name: `${profile.lastName}, ${profile.firstName}`,
            fileAs: `${profile.lastName}, ${profile.firstName}`,
            guid: profile.guid,
            metadata: {
              user: profile.user,
              email: profile.email
            },
            status: 'online',
            isActive: true
          }
        })
      }
    } catch (e) {
      console.log('Error (fetchChannel)', e)
    }
    return channel
  },
  async createChannel({ commit, dispatch }, data) {
    try {
      let profile = store.getters['auth/profile']
      // create the channel
      let options = null
      console.log('createChannel: direction', data)
      if (data.info.type === 'adhoc') {
        // if new adhoc, set up as a 'my' channel
        if (data.info.url === 'new') {
          options = {
            channelUrl: `${profile.apiName}_${profile.firm}_${profile.user}_${moment().unix()}`,
            name: data.info.name,
            customType: `adhoc_${profile.apiName}_${profile.firm}`,
            isDistinct: false,
            isPublic: false,
            invitedUserIds: data.info.members,
            operatorUserIds: [profile.guid]
          }
        }
      } else {
        // otherwise, it's an active request, but no channel exists
        options = {
          channelUrl: `${profile.apiName}_${data.info.metadata.firm}_${data.info.metadata.calibrate}_${data.info.metadata.calibrateEvent}`,
          name: data.info.metadata.calibrateEvent.toString(),
          customType: `request_${profile.apiName}_${data.info.metadata.firm}`,
          isDistinct: false,
          isPublic: true,
          invitedUserIds: [profile.guid]
        }
      }

      data.info.url = options.channelUrl
      // create the channel
      let newchannel = await window.sendbird.groupChannel.createChannel(options)
      console.log('Created Channel at Sendbird', newchannel)
      if (data.info.type !== 'adhoc') {
        // add the metadata, if a request channel
        await newchannel.createMetaData({
          calibrate: data.info.metadata.calibrate.toString(),
          calibrateEvent: data.info.metadata.calibrateEvent.toString(),
          firm: data.info.metadata.firm.toString(),
          firmName: data.info.metadata.firmName,
          make: data.info.metadata.make,
          model: data.info.metadata.model,
          ronumber: data.info.metadata.ronumber,
          vin: data.info.metadata.vin,
          year: data.info.metadata.year.toString()
        })
      }
      console.log('Returning Created Channel at Sendbird', newchannel)
      let channelInfo = null
      if (newchannel) {
        let members = []
        newchannel.members.forEach(member => {
          members.push({
            name: member.nickname,
            userId: member.userId,
            metadata: member.metaData,
            lastSeenAt: member.lastSeenAt,
            isActive: member.isActive,
            connectionStatus: member.connectionStatus,
            state: member.state
          })
        })
        channelInfo = {
          createdOn: newchannel.createdAt,
          data: '',
          memberCount: newchannel.memberCount,
          members: members,
          metadata: newchannel.cachedMetaData,
          name: newchannel.name,
          owner: profile.guid,
          type: newchannel.customType,
          unreadCount: 0,
          url: newchannel.url
        }
      }
      commit(types.SET_CHANNELINFO, channelInfo)
      if (data.refresh) {
        // refresh channels
        store.dispatch('chat/getChannels')
      }
      console.log('Returning Created Channel at Sendbird', {
        channel: newchannel,
        channelInfo: channelInfo
      })
      return newchannel
    } catch (e) {
      console.log('Error (createChannel)', e)
    }
  },
  joinChannel({ commit }, url) {
    commit(types.JOIN_CHANNEL, url)
  },
  leaveChannel({ commit }, url) {
    commit(types.LEAVE_CHANNEL, url)
  },
  async deleteChannel({ commit }, url) {
    console.log('Delete Channel', url)
    if (url) {
      let channel = await window.sendbird.groupChannel.getChannel(url)
      await channel.delete()
      // refresh channels
      await store.dispatch('chat/getChannels')
    }
  },
  async getChannelMessages({ commit, state }, info) {
    let profile = store.getters['auth/profile']
    commit(types.RESET_MESSAGES)
    try {
      if (info) {
        let channel = await store.dispatch('chat/fetchChannel', info)
        console.log('Get Messages - Channel', {
          channelInfo: state.channelInfo,
          channel: channel
        })
        if (channel) {
          let rawMessages = []
          let messageCollection = channel.createPreviousMessageListQuery()
          do {
            // Load the next page of messages
            const messages = await messageCollection.load(30) // You can adjust the limit based on your requirements
            // Append the loaded messages to the result array
            rawMessages = rawMessages.concat(messages)
            // Update the query for the next iteration if there are more messages
            if (messageCollection.hasMore) {
              messageCollection = channel.createPreviousMessageListQuery()
              messageCollection = messageCollection.prev(messages[messages.length - 1].messageId)
            }
          } while (messageCollection.hasMore)
          console.log('Fetched Messages', rawMessages)
          // populate our messages
          rawMessages.forEach(message => {
            commit(types.SET_A_MESSAGE, {
              id: message.messageId,
              name: null,
              createdOn: moment(message.createdAt).utc().format('MM/DD/YYYY h:mm:ss a'),
              type: message.sender.userId === profile.guid ? 'received' : 'sent',
              color: null,
              message: message.message,
              member: message.sender.nickname,
              guid: message.sender.userId
            })
          })
          // since the user is viewing messages, mark the channel as read
          channel.markAsRead()
          console.log('Got Messages', state.messages)
          await store.dispatch('chat/getChannels')
        }
      }
    } catch (e) {
      console.log('Error (getChannelMessages)', e)
    }
  },
  async createChannelSendMessage({ commit }, info) {
    let newchannel = null
    if (info.url === 'new' && info.type === 'adhoc') {
      console.log('Calling Create Channel (createChannelSendMessage)', info)
      // creating an adhoc channel and its first message
      newchannel = await store.dispatch('chat/createChannel', {
        info: info,
        refresh: false
      })
      if (newchannel) {
        console.log('Channel Created', newchannel)
        let data = {
          message: info.message
        }
        // now, we can send the message
        let result = await newchannel.sendUserMessage(data)
        console.log('Sent Message', result)
      }
    }
    return newchannel
  },
  async sendMessage({ commit, state }, message) {
    try {
      let profile = store.getters['auth/profile']
      console.log('Sending Message', {
        message: message,
        channelInfo: state.channelInfo,
        url: state.channelInfo.url
      })
      // get the channel
      const channel = await window.sendbird.groupChannel.getChannel(state.channelInfo.url)
      // prep the message
      let data = {
        message: message
      }
      // send the message
      let result = await channel.sendUserMessage(data)
      // capture in our messages array
      commit(types.SET_A_MESSAGE, {
        id: null,
        name: null,
        color: null,
        createdOn: moment().format('MM/DD/YYYY hh:mm:ss a'),
        message: message,
        type: 'received',
        member: `${profile.lastName}, ${profile.firstName}`,
        guid: profile.guid
      })
      console.log('Sent Message', result)
    } catch (e) {
      console.log('sendMessage', e)
    }
  },
  async memberTyping({ commit }, data) {
    const channel = await window.sendbird.groupChannel.getChannel(data.url)
    if (data.status === 'typing_on') {
      await channel.startTyping()
    } else {
      await channel.endTyping()
    }
  },
  async searchChatUsers({ commit, dispatch }, data) {
    commit(types.RESET_FOUND_MEMBERS)
    const r = await startLoading(dispatch, 'searchChatUsers', () => http.get(`chat/searchUsers?firm=${data.firm}&search=${data.search}`))
    commit(types.SET_FOUND_MEMBERS, r.data)
    return r.data
  },
  async searchChatMembers({ commit, state }, search) {
    commit(types.RESET_FOUND_MEMBERS)
    let fetchedMembers = []
    if (search) {
      if (search.trim() !== '') {
        let params = {
          nicknameStartsWithFilter: search
        }
        let collection = await window.sendbird.createApplicationUserListQuery(params)
        do {
          const members = await collection.next()
          fetchedMembers = fetchedMembers.concat(members)
          // Update the query for the next iteration if there are more channels
          if (collection.hasNext) {
            collection = collection.next()
          }
        } while (collection.hasNext)
        console.log('Got Fetched Members', fetchedMembers)
        fetchedMembers.forEach(member => {
          if (JSON.stringify(member.metaData) !== '{}') {
            commit(types.SET_FOUND_MEMBER, {
              label: member.metaData ? `${member.nickname} (${member.metaData.email})` : member.nickname,
              guid: member.userId,
              emailAddress: member.metaData ? member.metaData.email : null,
              fullName: member.nickname,
              name: member.nickname,
              user: member.metaData ? member.metaData.user : null
            })
          }
        })
      }
    }
    console.log('Found Members', state.foundMembers)
    return state.foundMembers
  },
  async setChannelMembers({ commit, state }, data) {
    if (data) {
      if (data.url) {
        const channel = await window.sendbird.groupChannel.getChannel(data.url)
        if (channel && data.members) {
          if (data.members.length > 0) {
            await channel.inviteWithUserIds(data.members)
          }
        }
      }
    }
  },
  async reportMessage({ commit, dispatch }, report) {
    const r = await startLoading(dispatch, 'reportMessage', () => http.post('chat/reportMessage', report))
    return r.data
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/chat/actions.js